/**
 * Плашки под шапкой (§11.7 и §6.6 спеки).
 *
 * Три штуки, все появляются только по делу:
 *   0. «Правки не сохранились» — сеть моргнула, правка не дошла до сервера.
 *      Она не потеряна: лежит в очереди и уходит сама, а плашка не гаснет,
 *      пока очередь не опустеет. Это единственный случай, когда данные
 *      существуют только в браузере, и молчать о нём нельзя.
 *   1. «Данные обновлены» — кто-то опубликовал пакет, пока мы работали.
 *      Свой черновик при обновлении не трогается, об этом сказано прямо.
 *   2. «У вас остался черновик» — напоминание о брошенных правках, один раз
 *      за сессию (обещание «ни одно изменение не теряется» должно работать).
 */
import { useEffect, useState, useSyncExternalStore } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/index.js'
import { useApp } from '../store/app-store.js'
import { flushPending, pendingEdits, subscribePending } from '../store/retry-queue.js'
import { countOf, forms } from '../texts.js'
import './AppBanners.css'

const DRAFT_REMINDER_SHOWN = 'bm.draftReminderShown'

export function AppBanners() {
  const navigate = useNavigate()
  const staleRevision = useApp((s) => s.staleRevision)
  const lastCommitAuthorId = useApp((s) => s.lastCommitAuthorId)
  const users = useApp((s) => s.bootstrap?.users ?? [])
  const reloadBrands = useApp((s) => s.reloadBrands)
  const draftCount = useApp((s) => s.draftChanges.length)

  const [reminderDismissed, setReminderDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DRAFT_REMINDER_SHOWN) === '1'
    } catch {
      return false
    }
  })

  // Напоминание показываем один раз за сессию — дальше оно только мешает.
  useEffect(() => {
    if (!reminderDismissed && draftCount > 0) {
      try {
        sessionStorage.setItem(DRAFT_REMINDER_SHOWN, '1')
      } catch {
        /* приватный режим — переживём без памяти */
      }
    }
  }, [draftCount, reminderDismissed])

  const author = users.find((u) => u.id === lastCommitAuthorId)

  /* Очередь несохранённых правок живёт вне React (localStorage + таймер),
     поэтому подписываемся на неё напрямую. */
  const pending = useSyncExternalStore(subscribePending, pendingEdits)

  return (
    <>
      {pending.length > 0 && (
        <div className="banner banner--pending" role="alert">
          <span className="banner__text">
            {countOf(pending.length, forms.change)} не ушли на сервер: {
              [...new Set(pending.map((edit) => edit.label))].slice(0, 3).join(', ')
            }. Они сохранены здесь и отправляются снова — не закрывайте вкладку.
          </span>
          <Button size="s" variant="secondary" onClick={() => void flushPending()}>
            Повторить сейчас
          </Button>
        </div>
      )}
      {staleRevision !== null && (
        <div className="banner banner--fresh" role="status">
          <span className="banner__text">
            {author ? `${author.shortName} опубликовал изменения` : 'Данные обновлены'} · ваш черновик не затронут
          </span>
          <Button size="s" variant="secondary" onClick={() => void reloadBrands()}>
            Обновить
          </Button>
        </div>
      )}

      {!reminderDismissed && draftCount > 0 && (
        <div className="banner banner--draft" role="status">
          <span className="banner__text">
            У вас остался неподтверждённый черновик: {countOf(draftCount, forms.change)}. Его никто не видит, пока вы не нажмёте «Подтвердить изменения».
          </span>
          <Button size="s" variant="secondary" onClick={() => navigate('/draft')}>
            Посмотреть
          </Button>
          <Button size="s" variant="ghost" onClick={() => setReminderDismissed(true)}>
            Скрыть
          </Button>
        </div>
      )}
    </>
  )
}
